import {useForm} from '@tanstack/react-form';
import * as React from 'react';
import {Button} from '~/components/Button';
import {EditorField, FormColumn, FormControls, FormRow, FormTitle, IntentText} from '~/components/Form';
import formStyle from '~/components/Form/index.module.scss';
import {IModalComponent} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {getApiErrorMessage, getApiProblem} from '~/lib/errors/apiError';
import {IPreTargetingProbsError, markedSourceLine} from '~/modules/Bidders/models/PreTargeting';
import {Intent} from '~/lib/intent';
import {useResource} from '~/lib/loader';
import {ID} from '~/lib/model';
import {fetchPreTargetingRulesText, updatePreTargetingRulesText} from '~/modules/Bidders/api';
import {BidderFormCtx, Field} from '~/modules/Bidders/forms/BidderFormBridge';
import style from './PreTargetingRulesEditorForm.module.scss';
import AvailableAttributes from '~/modules/Bidders/components/AvailableAttributes';

interface IPreTargetingRulesEditorForm {
    preTargetingId?: ID;
    preTargetingTitle?: string;
}

const FormShell = (props: React.FormHTMLAttributes<HTMLFormElement>) => {
    const classes = [formStyle.form, props.className].filter(Boolean);
    const childrenArray = React.Children.toArray(props.children);
    const titles: React.ReactNode[] = [];
    const content: React.ReactNode[] = [];
    const controls: React.ReactNode[] = [];

    childrenArray.forEach(child => {
        if (!React.isValidElement(child)) {
            content.push(child);
            return;
        }
        if (child.type === FormControls) {
            controls.push(child);
        } else if (child.type === FormTitle) {
            titles.push(child);
        } else {
            content.push(child);
        }
    });

    return <form {...props} className={classes.join(' ')}>
        {titles}
        <div className={formStyle.formBody}>
            {content}
        </div>
        {controls}
    </form>;
};

export const PreTargetingRulesEditorForm: IModalComponent<IPreTargetingRulesEditorForm> = props => {
    const {isPending, resource: rules} = useResource(() => fetchPreTargetingRulesText(props.preTargetingId));

    if (isPending) {
        return <div className={style.rulesLoadProgress}><Progress/></div>;
    }

    return <PreTargetingRulesEditorFormInner {...props} initialRules={rules ?? ''} />;
};

const PreTargetingRulesEditorFormInner = (props: IPreTargetingRulesEditorForm & {initialRules: string; close?: () => void}) => {
    const closeModal = props.close ?? (() => undefined);
    const [status, setStatus] = React.useState<string>();
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, any>>({});
    const [syntaxError, setSyntaxError] = React.useState<string>();

    const subtitleParts: string[] = [];
    if (props.preTargetingTitle) {
        subtitleParts.push(props.preTargetingTitle);
    }
    if (props.preTargetingId != null) {
        subtitleParts.push(`ID: ${props.preTargetingId}`);
    }
    const subtitle = subtitleParts.join(' · ');

    const form = useForm({
        defaultValues: {
            rules: props.initialRules
        },
        onSubmit: async ({value}) => {
            try {
                await updatePreTargetingRulesText(props.preTargetingId, value.rules);
                closeModal();
            } catch (error: any) {
                const problem = getApiProblem<IPreTargetingProbsError>(error);
                const marked = problem && markedSourceLine(value.rules, problem);
                const message = getApiErrorMessage(error, 'Failed to save rules');
                setStatus(undefined);
                setSyntaxError(marked ? `${marked}\n${message}` : undefined);
                setFieldErrors(marked ? {} : {rules: message});
                throw error;
            }
        }
    });

    return <form.Subscribe selector={state => state}>
        {state => {
            const setFieldValue = (fieldName: string, fieldValue: unknown) => {
                setStatus(undefined);
                form.setFieldValue(fieldName as never, fieldValue as never);
            };

            const formBridge = {
                errors: fieldErrors,
                touched: {},
                submitCount: state.submissionAttempts,
                setFieldValue,
                setStatus,
                setSubmitting: (_submitting: boolean) => undefined,
            };

            const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
                event?.preventDefault();
                void form.handleSubmit();
            };

            return <BidderFormCtx.Provider value={{form, formBridge}}>
                <FormShell onSubmit={handleSubmit}>
                    <FormTitle>Rules</FormTitle>
                    {subtitle && <div className={style.rulesSubtitle}>{subtitle}</div>}
                    {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                    <FormRow>
                        <FormColumn>
                            <Field name='rules'
                                options={{
                                    disabled: state.isSubmitting,
                                    mode: 'javascript',
                                    canFullScreen: true,
                                    maxLines: 20,
                                    transform: (value: string) => value.replace(/”|”/g, '”'),
                                }}
                                component={EditorField}
                            />
                            {syntaxError && <pre className={style.rulesSyntaxError}>{syntaxError}</pre>}
                            <AvailableAttributes />
                        </FormColumn>
                    </FormRow>
                    <FormControls>
                        <Button disabled={state.isSubmitting}
                            onClick={closeModal}
                        >Cancel</Button>
                        <Button type='submit'
                            disabled={state.isSubmitting}
                            intent={Intent.PRIMARY}
                            rightIcon={state.isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                        >Save</Button>
                    </FormControls>
                </FormShell>
            </BidderFormCtx.Provider>;
        }}
    </form.Subscribe>;
};
